<template>
  <PreviewElementWrapper 
  :initial-selected="isSelected"
  :element-type="element.type"
  :element="element"
  :selectedPreviewId="selectedPreviewId"
  :disabled="disabled"
  @mouseenter="isHovered = true"
  @mouseleave="isHovered = false"
  @select-change="handleSelectChange"
  @update-element-data="handleWrapperDataUpdate"
  @auto-mapping="(id)=>$emit('auto-mapping', id)"
  @element-edit-state-changed="$emit('element-edit-state-changed', $event)"
  >
    <div class="field-input-container" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
      <span class="placeholder-text">0</span>
      <FieldTooltip :visible="showTooltip" />
    </div>
    <div v-if="isHovered || isSelected" class="limit-toggle">
      <span class="limit-label">Set Value Limit</span>
      <div :class="['toggle-switch', { 'active': isValueLimitOn }]">
        <div class="toggle-label">No</div>
        <div class="toggle-track" @click.stop="handleToggleClick">
          <div class="toggle-thumb"></div>
        </div>
        <div class="toggle-label">Yes</div>
      </div>
    </div>
    <div v-if="isValueLimitOn && (isHovered || isSelected)" class="value-limit-input-container">
      <div class="field-input-item">
        <span>Min</span>
        <div class = "field-input">
            <VAInput
              v-model="minValue"
              placeholder="0"
            />
        </div>
      </div>
      <div class="field-input-item">
        <span>Max</span>
        <div class = "field-input">
          <VAInput
            v-model="maxValue"
            placeholder="100"
          />
        </div>
      </div>
    </div>
  </PreviewElementWrapper>
</template>

<script>
import PreviewElementWrapper from './PreviewElementWrapper.vue';
import FieldTooltip from './FieldTooltip.vue';

export default {
  name: 'NumericPreview',
  components: { PreviewElementWrapper, FieldTooltip },
  props: {
    element: { type: Object, default: () => ({}) },
    selectedPreviewId: { type: String, default: '' },
    disabled: { type: Boolean, default: false }
  },
  data() {
    const elemData = (this.element && this.element.data) || {};
    return {
      showTooltip: false,
      isValueLimitOn: elemData.isLimited || false,
      minValue: elemData.min != null ? String(elemData.min) : '',
      maxValue: elemData.max != null ? String(elemData.max) : '',
      isSelected: false,
      isHovered: false,
    };
  },
  emits: [
    'update-element-data',
    'auto-mapping',
    'element-edit-state-changed'
  ],
  watch: {
    isValueLimitOn(newVal) { 
      if(newVal) {
        this.minValue = this.minValue || '0';
        this.maxValue = this.maxValue || '100';
        this.emitDataUpdate(); 
      }else{
        this.minValue = null;
        this.maxValue = null;
        this.emitDataUpdate();
      }
    },
    minValue() { 
      this.emitDataUpdate(); 
    },
    maxValue() { 
      this.emitDataUpdate(); 
    }
  },
  methods: {
    handleSelectChange(selected) {
      this.isSelected = selected;
      if(!selected) {
        this.emitDataUpdate();
      }
    },
    handleToggleClick() {
      this.isValueLimitOn = !this.isValueLimitOn;
      this.isSelected = true;
    },
    emitDataUpdate() {
      const data = {
        isLimited: this.isValueLimitOn,
        min: this.isValueLimitOn && this.minValue !== '' ? Number(this.minValue) : null,
        max: this.isValueLimitOn && this.maxValue !== '' ? Number(this.maxValue) : null
      };
      this.$emit('update-element-data', data);
    },
    handleWrapperDataUpdate(wrapperData) {
      this.$emit('update-element-data', { ...wrapperData, min: this.isValueLimitOn && this.minValue !== '' ? Number(this.minValue) : null, max: this.isValueLimitOn && this.maxValue !== '' ? Number(this.maxValue) : null });
    }
  }
};
</script>

<style scoped>
.field-input-container {
  position: relative;
  background: #fff;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
}

.placeholder-text {
  color: #999;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
  padding: 0 8px;
}

.limit-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 22px;
}

.limit-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-track {
  width: 40px;
  height: 22px;
  background: #F9FBFD;
  border: 1px solid #374957;
  border-radius: 13px;
  padding: 2px;
  display: flex;
  align-items: center;
}
.toggle-thumb {
  width: 18px;
  height: 18px;
  background: #374957;
  border-radius: 50%;
  transition: all 0.5s ease;
}
.toggle-switch .toggle-track:hover {
  background: #E8F4FD;
  border-color: #1E4670;
}
.toggle-switch .toggle-track:hover .toggle-thumb {
  background: #004879;
}
.active .toggle-track {
  background: #374957;
}
.active .toggle-thumb {
  transform: translateX(calc(100% - 2px));
  background: linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%);
}
.toggle-switch.active .toggle-track:hover {
  background: #004879;
  border-color: #004879;
}
.toggle-switch.active .toggle-track:hover .toggle-thumb {
  background: linear-gradient(90deg, #E6EDF2 0%, #EAF8F9 100%);
}

.toggle-label {
  color: #4A4A4A;

  /* EHR_BodyM_14_Web */
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: 0.25px;
}

.value-limit-input-container {
  padding: 0 64px;
  display: flex;
  justify-content: center;
  gap: 16px;
}
.field-input-item {
  width: 50%;
  display: flex;
  align-items: center;
  gap: 4px;
}
.field-input-item > span {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4A4A4A;
}
</style>
